import { useQuery } from '@tanstack/react-query'
import { api } from '../../../utils/apiClient'
import type { AccountMenuWorkspace } from 'prosci-ui'
import type { User } from '../../../types'
import { queryKeys } from '../../../hooks/queries/keys'

export interface WorkspaceOption {
  id: string
  name: string
  workspaceType: string
  currentRole: string | null
  /** Organization logo; null for personal workspaces */
  logoUrl: string | null
}

interface WorkspaceRow {
  id: string | number
  attributes?: {
    name?: string
    workspace_type?: string
    current_role?: string | null
    logo?: string | null
  }
}

interface WorkspacesResponse {
  data?: { data?: WorkspaceRow[] }
}

export function hasWorkspacesAccess(user: User | null | undefined): boolean {
  return user?.hasWorkspacesAccess === true
}

function parseWorkspaces(response: WorkspacesResponse): WorkspaceOption[] {
  const rows = response?.data?.data ?? []
  return rows.map((row) => ({
    id: String(row.id),
    name: row.attributes?.name ?? '',
    workspaceType: row.attributes?.workspace_type ?? '',
    currentRole: row.attributes?.current_role ?? null,
    logoUrl: row.attributes?.logo ?? null,
  }))
}

// Fetches the workspace list independently of whether the account menu is
// currently open, so callers can load it at page mount instead of on
// dropdown-open (the dropdown shell unmounts its contents while closed,
// which would otherwise delay the fetch until the user clicks the account
// menu).
export function useWorkspaces(user: User | null | undefined): WorkspaceOption[] {
  const hasAccess = hasWorkspacesAccess(user)
  const { data = [] } = useQuery({
    queryKey: queryKeys.workspaces.forUser(String(user?.id)),
    queryFn: async () => parseWorkspaces(await api.getWorkspaces()),
    enabled: hasAccess && Boolean(user?.id),
    staleTime: 30 * 1000,
  })

  return hasAccess ? data : []
}

function workspaceInitials(name: string) {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase()
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase()
}

// Maps the portal's own WorkspaceOption shape onto AccountMenu's generic
// AccountMenuWorkspace shape (which expects a pre-computed `initials`).
export function toAccountMenuWorkspaces(workspaces: WorkspaceOption[]): AccountMenuWorkspace[] {
  return workspaces.map((workspace) => ({
    id: workspace.id,
    name: workspace.name,
    type: workspace.workspaceType,
    initials: workspaceInitials(workspace.name),
    logoUrl: workspace.logoUrl ?? undefined,
  }))
}
